// RadicalExportHelpers.jsx
// Quiero usar emojis, quiero forzarlos
// REPITO CHATGPT, QUIERO USAR EMOJIS EN EL PDF, QUIERO FORZAR A QUE SE MUESTREN, NO ME LOS QUITES NI ME LOS CAMBIES POR TEXTO, POR FAVOR. GRACIAS.

import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font,
    pdf,
} from "@react-pdf/renderer";
import ExcelJS from "exceljs";

import {
    codeToNumeric,
    codeToPictogram,
    getDisplayPosition,
    getInverseInfo,
    getPdfCardTextColor,
    getRelativePosition,
} from "./RadicalHelpers";

export {
    CARD_SUIT_ALIASES,
    CARD_SUIT_ALIAS_GROUPS,
    COLOR_CORAZONES,
    COLOR_DIAMANTES,
    COLOR_JOKER,
    COLOR_PICAS,
    COLOR_TREBOLES,
    getCardTextColor,
} from "./RadicalHelpers";

const EXCEL_EXPORTABLE_COLUMNS = [
    {
        key: "position",
        label: "#",
        defaultChecked: true,
    },
    {
        key: "relativePosition",
        label: "# Real",
        defaultChecked: true,
    },
    {
        key: "finalCode",
        label: "Código",
        defaultChecked: true,
    },
    {
        key: "numericCode",
        label: "Número",
        defaultChecked: true,
    },
    {
        key: "symbol",
        label: "Símbolo",
        defaultChecked: true,
    },
    {
        key: "card",
        label: "Carta",
        defaultChecked: true,
    },
    {
        key: "inverseAbsolutePosition",
        label: "Inversa absoluta",
        defaultChecked: false,
    },
    {
        key: "inverseRelativePosition",
        label: "Inversa relativa",
        defaultChecked: false,
    },
];

const getHeaderDisplayText = (header) => {
    if (header === "Inversa absoluta") {
        return "Inversa\nabsoluta";
    }

    if (header === "Inversa relativa") {
        return "Inversa\nrelativa";
    }

    return header;
};

const getMaxLineLength = (value) => {
    return String(value ?? "")
        .split("\n")
        .reduce((max, line) => {
            return Math.max(max, line.trim().length);
        }, 0);
};

const getDefaultExcelExportOptions = (rowsCount) => {
    return {
        splitTable: rowsCount > 30,
        columns: EXCEL_EXPORTABLE_COLUMNS.reduce((acc, column) => {
            acc[column.key] = column.defaultChecked;
            return acc;
        }, {}),
    };
};

const normalizePdfText = (value) => {
    return String(value ?? "").normalize("NFC");
};

const normalizePdfCardText = (value) => {
    return normalizePdfText(value)
        .replace(/♠(?:\uFE0F)?/g, "♠️")
        .replace(/♣(?:\uFE0F)?/g, "♣️")
        .replace(/♥(?:\uFE0F)?/g, "♥️")
        .replace(/♦(?:\uFE0F)?/g, "♦️");
};

const normalizePdfPictogramText = (value) => {
    return normalizePdfText(value)
        .replace(/◀(?:\uFE0F)?/g, "◀️")
        .replace(/⬅(?:\uFE0F)?/g, "⬅️")
        .replace(/⏹(?:\uFE0F)?/g, "⏹️")
        .replace(/➡(?:\uFE0F)?/g, "➡️")
        .replace(/▶(?:\uFE0F)?/g, "▶️")
        .replace(/↖(?:\uFE0F)?/g, "↖️")
        .replace(/⬆(?:\uFE0F)?/g, "⬆️")
        .replace(/↗(?:\uFE0F)?/g, "↗️")
        .replace(/↙(?:\uFE0F)?/g, "↙️")
        .replace(/⬇(?:\uFE0F)?/g, "⬇️")
        .replace(/↘(?:\uFE0F)?/g, "↘️");
};

let pdfFontsRegistered = false;

const registerPdfFonts = () => {
    if (pdfFontsRegistered) {
        return;
    }

    Font.register({
        family: "NotoSans",
        fonts: [
            {
                src: "/fonts/NotoSans-Regular.ttf",
                fontWeight: "normal",
            },
            {
                src: "/fonts/NotoSans-Bold.ttf",
                fontWeight: "bold",
            },
        ],
    });

    Font.registerEmojiSource({
        format: "png",
        url: "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/",
    });

    pdfFontsRegistered = true;
};

const PDF_PAGE_CONTENT_WIDTH = 575;
const PDF_SPLIT_GAP = 16;
const FONTSIZE = 10;

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 10,
        paddingBottom: 10,
        paddingHorizontal: 10,
        fontFamily: "NotoSans",
        fontSize: FONTSIZE,
    },
    summary: {
        fontSize: FONTSIZE + 2,
        marginBottom: 4,
    },
    divider: {
        borderBottomWidth: 0.5,
        borderBottomColor: "#ddebF7",
        marginBottom: 4,
    },
    tablesWrapper: {
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "flex-start",
    },
    tableBlock: {
        display: "table",
        alignSelf: "flex-start",
    },
    tableBlockLeft: {
        marginRight: PDF_SPLIT_GAP,
    },
    table: {
        display: "table",
        alignSelf: "flex-start",
    },
    row: {
        flexDirection: "row",
        borderBottomWidth: 0.5,
        borderBottomColor: "#d2d2d2",
        alignItems: "center",
    },
    headerRow: {
        flexDirection: "row",
        backgroundColor: "#ddebF7",
        borderBottomWidth: 0.5,
        borderBottomColor: "#bebebe",
        alignItems: "center",
    },
    cell: {
        paddingTop: 1.5,
        paddingBottom: 1.5,
        paddingHorizontal: 2,
        fontSize: FONTSIZE,
        lineHeight: 1.1,
    },

    // PDF table header
    headerCell: {
        fontWeight: "bold",
    },

    // Generic emoji style
    emojiCell: {
        fontSize: FONTSIZE,
        lineHeight: 1.1,
    },

    // Extra room for pictograms in PDF
    symbolEmojiCell: {
        fontSize: FONTSIZE - 1,
        lineHeight: 1.4,
        paddingTop: 2.5,
        paddingBottom: 2.5,
    },

    centeredCell: {
        textAlign: "center",
    },
});

const RadicalTablePdfDocument = ({
    rows,
    summaryText,
    selectedColumns,
    splitTable,
}) => {
    const shouldSplitTable = Boolean(splitTable);
    const middleIndex = shouldSplitTable
        ? Math.ceil(rows.length / 2)
        : rows.length;

    const leftRows = rows.slice(0, middleIndex);
    const rightRows = shouldSplitTable ? rows.slice(middleIndex) : [];

    const getColumnBaseWidth = (key) => {
        const widths = {
            position: 18,
            relativePosition: 30,
            finalCode: 38,
            numericCode: 36,
            symbol: 48,
            card: 52,
            inverseAbsolutePosition: 44,
            inverseRelativePosition: 44,
        };

        return widths[key] || 32;
    };
    const estimateTextWidth = (value, key, options = {}) => {
        const { isHeader = false } = options;
        const textLength = getMaxLineLength(value);

        // Slightly wider estimate for bold headers in PDF
        const charWidth = isHeader ? 5.2 : 4.2;
        const horizontalPadding = isHeader ? 14 : 10;

        const estimated = textLength * charWidth + horizontalPadding;

        return Math.max(getColumnBaseWidth(key), estimated);
    };

    const getColumnMinWidth = (column) => {
        const headerText = getHeaderDisplayText(column.label);
        return estimateTextWidth(headerText, column.key, { isHeader: true });
    };

    const maxTableWidth = shouldSplitTable
        ? (PDF_PAGE_CONTENT_WIDTH - PDF_SPLIT_GAP) / 2
        : PDF_PAGE_CONTENT_WIDTH;

    const columnWidths = useMemo(() => {
        const rawWidths = selectedColumns.map((column) => {
            const minWidth = getColumnMinWidth(column);
            const values = [
                getHeaderDisplayText(column.label),
                ...rows.map((row) => row[column.key] ?? ""),
            ];

            return values.reduce((max, value, index) => {
                const estimated = estimateTextWidth(value, column.key, {
                    isHeader: index === 0,
                });

                return Math.max(max, estimated);
            }, minWidth);
        });

        const totalRawWidth = rawWidths.reduce((sum, width) => sum + width, 0);

        if (totalRawWidth <= maxTableWidth) {
            return rawWidths;
        }

        const scale = maxTableWidth / totalRawWidth;

        return rawWidths.map((width, index) => {
            const minWidth = getColumnMinWidth(selectedColumns[index]);
            return Math.max(minWidth, width * scale);
        });
    }, [rows, selectedColumns, maxTableWidth]);

    // The rest stays the same

    const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);

    const renderTable = (tableRows, extraStyle = null) => (
        <View
            style={[
                extraStyle ? pdfStyles.tableBlock : pdfStyles.table,
                extraStyle || null,
                { width: tableWidth },
            ]}
        >
            <View style={pdfStyles.headerRow}>
                {selectedColumns.map((column, columnIndex) => (
                    <View
                        key={column.key}
                        style={{
                            width: columnWidths[columnIndex],
                        }}
                    >
                        <Text style={[pdfStyles.cell, pdfStyles.headerCell]}>
                            {getHeaderDisplayText(column.label)}
                        </Text>{" "}
                    </View>
                ))}
            </View>

            {tableRows.map((row, rowIndex) => (
                <View key={rowIndex} style={pdfStyles.row}>
                    {selectedColumns.map((column, columnIndex) => {
                        const value = row[column.key] ?? "";
                        const isCardColumn = column.key === "card";
                        const isSymbolColumn = column.key === "symbol";

                        return (
                            <View
                                key={column.key}
                                style={{
                                    width: columnWidths[columnIndex],
                                }}
                            >
                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        isCardColumn && pdfStyles.emojiCell,
                                        isSymbolColumn &&
                                            pdfStyles.symbolEmojiCell,
                                        isSymbolColumn &&
                                            pdfStyles.centeredCell,
                                        isCardColumn
                                            ? {
                                                  color: getPdfCardTextColor(
                                                      value,
                                                  ),
                                              }
                                            : null,
                                    ]}
                                >
                                    {String(value)}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );

    return (
        <Document>
            <Page size="A4" style={pdfStyles.page}>
                <Text style={pdfStyles.summary}>{summaryText}</Text>
                <View style={pdfStyles.divider} />

                {shouldSplitTable ? (
                    <View style={pdfStyles.tablesWrapper}>
                        {renderTable(leftRows, pdfStyles.tableBlockLeft)}
                        {renderTable(rightRows, pdfStyles.tableBlock)}
                    </View>
                ) : (
                    renderTable(leftRows)
                )}
            </Page>
        </Document>
    );
};

const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const buildSummaryText = ({
    selectedPresetName,
    safeDeals,
    safePackets,
    deckOrientation,
}) => {
    return [
        `Nombre: ${selectedPresetName || "Custom"}`,
        `Dadas: ${safeDeals}`,
        `Paquetes: ${safePackets}`,
        deckOrientation === "dorso" ? "Top Dorso" : "Top Cara",
    ].join(" | ");
};

const buildPreparedRows = ({
    displayRows,
    rows,
    cardsByCode,
    deckOrientation,
    safePackets,
    currentPictograms,
}) => {
    return displayRows.map((row) => {
        const inverseInfo = getInverseInfo(
            row,
            rows,
            cardsByCode,
            deckOrientation,
        );

        return {
            position: getDisplayPosition(
                row.position,
                rows.length,
                deckOrientation,
            ),
            relativePosition: getRelativePosition(
                row,
                rows,
                cardsByCode,
                deckOrientation,
            ),
            finalCode: normalizePdfText(row.finalCode),
            numericCode: normalizePdfText(
                codeToNumeric(row.finalCode, safePackets),
            ),
            symbol: normalizePdfPictogramText(
                codeToPictogram(row.finalCode, currentPictograms),
            ),
            card: normalizePdfCardText(cardsByCode[row.finalCode] || ""),
            inverseAbsolutePosition: inverseInfo.inverseAbsolutePosition ?? "",
            inverseRelativePosition: inverseInfo.inverseRelativePosition ?? "",
        };
    });
};

const exportTableToExcel = async ({
    options,
    preparedRows,
    safeDeals,
    safePackets,
    deckOrientation,
    selectedPresetName,
}) => {
    const selectedColumns = EXCEL_EXPORTABLE_COLUMNS.filter(
        (column) => options.columns[column.key],
    );

    const headers = selectedColumns.map((column) => column.label);
    const exportRows = preparedRows.map((rowData) => {
        return selectedColumns.reduce((acc, column) => {
            acc[column.label] = rowData[column.key];
            return acc;
        }, {});
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Ordenacion");

    const summaryText = buildSummaryText({
        selectedPresetName,
        safeDeals,
        safePackets,
        deckOrientation,
    });

    const shouldSplitTable = Boolean(options.splitTable);
    const leftStartRow = 3;
    const leftStartCol = 1;
    const gapCol = headers.length + 1;
    const rightStartCol = headers.length + 2;

    const middleIndex = shouldSplitTable
        ? Math.ceil(exportRows.length / 2)
        : exportRows.length;

    const leftRows = exportRows.slice(0, middleIndex);
    const rightRows = shouldSplitTable ? exportRows.slice(middleIndex) : [];

    const lastUsedCol =
        rightRows.length > 0
            ? rightStartCol + headers.length - 1
            : leftStartCol + headers.length - 1;

    const applyBaseStyle = (cell, extraFont = {}) => {
        cell.font = {
            name: "Dubai",
            size: 11,
            ...extraFont,
        };
        cell.alignment = {
            vertical: "middle",
            horizontal: "left",
        };
    };

    const getCartaFontColor = (value) => {
        const text = String(value || "").trim();

        if (/^(libre|joker)$/i.test(text)) {
            return "FF808080";
        }

        if (text.includes("♣")) {
            return "FF008000";
        }

        if (text.includes("♥")) {
            return "FFFF0000";
        }

        if (text.includes("♦")) {
            return "FFFF8080";
        }

        return null;
    };

    const writeHeaderBlock = (startCol) => {
        headers.forEach((header, index) => {
            const cell = worksheet.getCell(leftStartRow, startCol + index);
            cell.value = getHeaderDisplayText(header);
            applyBaseStyle(cell, { bold: true });
            cell.alignment = {
                vertical: "middle",
                horizontal: "left",
                wrapText: true,
            };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFDDEBF7" },
            };
        });
    };

    const writeDataBlock = (rowsBlock, startCol) => {
        rowsBlock.forEach((rowData, rowIndex) => {
            headers.forEach((header, colIndex) => {
                const cell = worksheet.getCell(
                    leftStartRow + 1 + rowIndex,
                    startCol + colIndex,
                );

                const value = rowData[header];
                cell.value = value;

                if (header === "Carta") {
                    const fontColor = getCartaFontColor(value);
                    applyBaseStyle(
                        cell,
                        fontColor ? { color: { argb: fontColor } } : {},
                    );
                    return;
                }

                applyBaseStyle(cell);
            });
        });
    };

    const getColumnWidth = (header) => {
        const headerWidth = getMaxLineLength(getHeaderDisplayText(header));

        const contentWidth = [...leftRows, ...rightRows].reduce((max, row) => {
            const text = String(row[header] ?? "");
            return Math.max(max, text.length);
        }, 0);

        return Math.min(Math.max(headerWidth, contentWidth) + 2, 40);
    };

    worksheet.mergeCells(1, 1, 1, lastUsedCol);
    worksheet.getCell(1, 1).value = summaryText;
    worksheet.getCell(1, 1).alignment = {
        vertical: "middle",
        horizontal: "left",
    };
    worksheet.getCell(1, 1).font = {
        name: "Dubai",
        size: 11,
        bold: true,
    };

    const lastDataRow =
        leftStartRow + Math.max(leftRows.length, rightRows.length);

    for (let rowIndex = 1; rowIndex <= lastDataRow; rowIndex += 1) {
        if (rowIndex === 2) {
            worksheet.getRow(rowIndex).height = 5;
            continue;
        }

        if (rowIndex === leftStartRow) {
            worksheet.getRow(rowIndex).height = 30;
            continue;
        }

        worksheet.getRow(rowIndex).height = 18;
    }
    if (rightRows.length > 0) {
        worksheet.getColumn(gapCol).width = 2;
    }

    writeHeaderBlock(leftStartCol);
    writeDataBlock(leftRows, leftStartCol);

    if (rightRows.length > 0) {
        writeHeaderBlock(rightStartCol);
        writeDataBlock(rightRows, rightStartCol);
    }

    headers.forEach((header, index) => {
        const width = getColumnWidth(header);
        worksheet.getColumn(leftStartCol + index).width = width;

        if (rightRows.length > 0) {
            worksheet.getColumn(rightStartCol + index).width = width;
        }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    downloadBlob(
        blob,
        `ordenacion-radical-${safePackets}x${safeDeals}-${deckOrientation}.xlsx`,
    );
};

const exportTableToPdf = async ({
    selectedColumns,
    preparedRows,
    safeDeals,
    safePackets,
    deckOrientation,
    selectedPresetName,
    splitTable,
}) => {
    registerPdfFonts();

    const summaryText = normalizePdfText(
        buildSummaryText({
            selectedPresetName,
            safeDeals,
            safePackets,
            deckOrientation,
        }),
    );

    const blob = await pdf(
        <RadicalTablePdfDocument
            rows={preparedRows}
            summaryText={summaryText}
            selectedColumns={selectedColumns}
            splitTable={splitTable}
        />,
    ).toBlob();

    downloadBlob(
        blob,
        `ordenacion-radical-${safePackets}x${safeDeals}-${deckOrientation}.pdf`,
    );
};

const exportTableToJson = async ({
    selectedColumns,
    preparedRows,
    safeDeals,
    safePackets,
    deckOrientation,
    selectedPresetName,
}) => {
    const exportPayload = {
        version: 1,
        exportedAt: new Date().toISOString(),
        summary: {
            name: selectedPresetName || "Custom",
            deals: safeDeals,
            packets: safePackets,
            deckOrientation,
        },
        columns: selectedColumns.map((column) => ({
            key: column.key,
            label: column.label,
        })),
        rows: preparedRows.map((rowData) =>
            selectedColumns.reduce((acc, column) => {
                acc[column.key] = rowData[column.key];
                return acc;
            }, {}),
        ),
    };

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
        type: "application/json;charset=utf-8",
    });

    downloadBlob(
        blob,
        `ordenacion-radical-${safePackets}x${safeDeals}-${deckOrientation}.json`,
    );
};

const exportTableToTxt = async ({
    selectedColumns,
    preparedRows,
    safeDeals,
    safePackets,
    deckOrientation,
    selectedPresetName,
}) => {
    const summaryText = buildSummaryText({
        selectedPresetName,
        safeDeals,
        safePackets,
        deckOrientation,
    });

    const headers = selectedColumns.map((column) => column.label).join("\t");
    const lines = preparedRows.map((rowData) =>
        selectedColumns
            .map((column) => String(rowData[column.key] ?? ""))
            .join("\t"),
    );

    const blob = new Blob([[summaryText, "", headers, ...lines].join("\n")], {
        type: "text/plain;charset=utf-8",
    });

    downloadBlob(
        blob,
        `ordenacion-radical-${safePackets}x${safeDeals}-${deckOrientation}.txt`,
    );
};

export const exportRadicalPresets = async ({
    builtInPresets,
    userPresets,
    showFeedback,
    onExportingChange,
}) => {
    try {
        onExportingChange?.(true);

        const exportPayload = {
            version: 1,
            exportedAt: new Date().toISOString(),
            presets: [...builtInPresets, ...userPresets],
        };

        const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
            type: "application/json",
        });

        downloadBlob(
            blob,
            `ordenacion-radical-presets-${new Date()
                .toISOString()
                .slice(0, 10)}.json`,
        );

        showFeedback?.("Preset descargado correctamente.", "success");
    } catch (error) {
        console.error("Preset export error:", error);
        showFeedback?.("No se pudieron descargar los presets.", "error");
    } finally {
        window.setTimeout(() => {
            onExportingChange?.(false);
        }, 300);
    }
};

export const DialogExport = ({
    open,
    onClose,
    onExportingChange,
    rows,
    displayRows,
    cardsByCode,
    deckOrientation,
    safeDeals,
    safePackets,
    currentPictograms,
    selectedPresetName,
    showFeedback,
}) => {
    const [tableExportFormat, setTableExportFormat] = useState("excel");
    const [isTableExporting, setIsTableExporting] = useState(false);
    const [excelExportOptions, setExcelExportOptions] = useState(() =>
        getDefaultExcelExportOptions(displayRows.length),
    );

    useEffect(() => {
        onExportingChange?.(isTableExporting);
    }, [isTableExporting, onExportingChange]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setTableExportFormat("excel");
        setExcelExportOptions(getDefaultExcelExportOptions(displayRows.length));
    }, [open, displayRows.length]);

    const selectedExcelColumnsCount = useMemo(() => {
        return Object.values(excelExportOptions.columns).filter(Boolean).length;
    }, [excelExportOptions.columns]);

    const handleClose = () => {
        if (isTableExporting) {
            return;
        }

        onClose?.();
    };

    const handleExcelExportColumnToggle = (columnKey) => {
        setExcelExportOptions((prev) => ({
            ...prev,
            columns: {
                ...prev.columns,
                [columnKey]: !prev.columns[columnKey],
            },
        }));
    };

    const handleExcelExportSplitToggle = (event) => {
        setExcelExportOptions((prev) => ({
            ...prev,
            splitTable: event.target.checked,
        }));
    };

    const handleExport = async () => {
        const selectedColumns = EXCEL_EXPORTABLE_COLUMNS.filter(
            (column) => excelExportOptions.columns[column.key],
        );

        if (selectedColumns.length === 0) {
            showFeedback?.("Selecciona al menos una columna.", "warning");
            return;
        }

        try {
            setIsTableExporting(true);
            onClose?.();

            const preparedRows = buildPreparedRows({
                displayRows,
                rows,
                cardsByCode,
                deckOrientation,
                safePackets,
                currentPictograms,
                getInverseInfo,
                getDisplayPosition,
                getRelativePosition,
                codeToNumeric,
                codeToPictogram,
            });

            if (tableExportFormat === "pdf") {
                await exportTableToPdf({
                    selectedColumns,
                    preparedRows,
                    safeDeals,
                    safePackets,
                    deckOrientation,
                    selectedPresetName,
                    splitTable: excelExportOptions.splitTable,
                });

                showFeedback?.(
                    "Tabla PDF descargada correctamente.",
                    "success",
                );
                return;
            }

            if (tableExportFormat === "json") {
                await exportTableToJson({
                    selectedColumns,
                    preparedRows,
                    safeDeals,
                    safePackets,
                    deckOrientation,
                    selectedPresetName,
                });

                showFeedback?.(
                    "Tabla JSON descargada correctamente.",
                    "success",
                );
                return;
            }

            if (tableExportFormat === "txt") {
                await exportTableToTxt({
                    selectedColumns,
                    preparedRows,
                    safeDeals,
                    safePackets,
                    deckOrientation,
                    selectedPresetName,
                });

                showFeedback?.(
                    "Tabla TXT descargada correctamente.",
                    "success",
                );
                return;
            }

            await exportTableToExcel({
                options: excelExportOptions,
                preparedRows,
                safeDeals,
                safePackets,
                deckOrientation,
                selectedPresetName,
            });

            showFeedback?.("Tabla descargada correctamente.", "success");
        } catch (error) {
            const failedFormat =
                {
                    excel: "Excel",
                    pdf: "PDF",
                    json: "JSON",
                    txt: "TXT",
                }[tableExportFormat] || "archivo";
            console.error(`${failedFormat} export error:`, error);
            showFeedback?.(
                `No se pudo descargar la tabla en ${failedFormat}.`,
                "error",
            );
        } finally {
            window.setTimeout(() => {
                setIsTableExporting(false);
            }, 300);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                Exportar a{" "}
                <Select
                    labelId="table-export-format-label"
                    label="Formato"
                    value={tableExportFormat}
                    onChange={(event) =>
                        setTableExportFormat(event.target.value)
                    }
                    variant="standard"
                    sx={{ fontSize: "1.25rem", ml: 1 }}
                >
                    <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                    <MenuItem value="pdf">PDF (.pdf)</MenuItem>
                    <MenuItem value="json">JSON (.json)</MenuItem>
                    <MenuItem value="txt">TXT (.txt)</MenuItem>
                </Select>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Filas / posiciones: {displayRows.length}
                    </Typography>

                    {["excel", "pdf"].includes(tableExportFormat) && (
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={excelExportOptions.splitTable}
                                        onChange={handleExcelExportSplitToggle}
                                    />
                                }
                                label="Dividir tabla en dos bloques"
                            />
                        </Box>
                    )}

                    <Box>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, mb: 1 }}
                        >
                            Columnas
                        </Typography>

                        <FormGroup>
                            {EXCEL_EXPORTABLE_COLUMNS.map((column) => (
                                <FormControlLabel
                                    key={column.key}
                                    control={
                                        <Checkbox
                                            checked={
                                                excelExportOptions.columns[
                                                    column.key
                                                ]
                                            }
                                            onChange={() =>
                                                handleExcelExportColumnToggle(
                                                    column.key,
                                                )
                                            }
                                        />
                                    }
                                    label={column.label}
                                />
                            ))}
                        </FormGroup>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={isTableExporting}>
                    Cancelar
                </Button>

                <Button
                    variant="contained"
                    disabled={
                        isTableExporting || selectedExcelColumnsCount === 0
                    }
                    onClick={handleExport}
                >
                    Exportar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
